import { api } from './api';
import { isFantasyAnswer } from '../gameplay/fantasyAnswers';
import type {
  FantasyAnswersPayload,
  FantasyAnswerValue,
  FantasyMatchEntry,
  FantasyMatchError,
  FantasyPartnerAnswers,
} from '../types/fantasyMatch';

/**
 * Everything the app knows about the Fantasy Match endpoints lives here, so a
 * change to the contract is a change to this file (and `fantasyMatchSlice`).
 */

export function toFantasyMatchError(error: unknown): FantasyMatchError {
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status;
  if (typeof status !== 'number') return { code: 'network', status: null };
  if (status === 429) return { code: 'rate_limited', status };
  return { code: 'unknown', status };
}

function toAnswerMap(raw: unknown): Record<string, FantasyAnswerValue> {
  const result: Record<string, FantasyAnswerValue> = {};
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return result;
  }
  for (const [slug, value] of Object.entries(raw)) {
    if (isFantasyAnswer(value)) result[slug] = value;
  }
  return result;
}

function toMatch(raw: unknown): FantasyMatchEntry | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.slug !== 'string' || item.slug === '') return null;
  if (!isFantasyAnswer(item.mine) || !isFantasyAnswer(item.partner)) {
    return null;
  }
  // A match is love / maybe / fantasy on both sides. Never keep a row with a `no`, even
  // if a server bug sent one: the partner's `no` must not reach the screen.
  if (item.mine === 'no' || item.partner === 'no') return null;
  return {
    slug: item.slug,
    mine: item.mine,
    partner: item.partner,
    kind:
      item.kind === 'both_love' || item.kind === 'fantasy'
        ? item.kind
        : 'curious',
  };
}

function toPartner(raw: unknown): FantasyPartnerAnswers | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const item = raw as Record<string, unknown>;
  return {
    name: typeof item.name === 'string' ? item.name : '',
    answered_count:
      typeof item.answered_count === 'number' ? item.answered_count : 0,
    share_all: item.share_all === true,
    answers: toAnswerMap(item.answers),
  };
}

/** Tolerant of a missing key: anything unusable becomes empty. */
export function normalizeFantasyAnswers(raw: unknown): FantasyAnswersPayload {
  const body =
    typeof raw === 'object' && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  return {
    mine: toAnswerMap(body.mine),
    partner: toPartner(body.partner),
    matches: Array.isArray(body.matches)
      ? body.matches
          .map(toMatch)
          .filter((match): match is FantasyMatchEntry => match !== null)
      : [],
    total_items: typeof body.total_items === 'number' ? body.total_items : 0,
    share_all: body.share_all === true,
  };
}

/** GET /fantasy/answers */
export async function requestFantasyAnswers(): Promise<FantasyAnswersPayload> {
  const response = await api.get('/fantasy/answers');
  return normalizeFantasyAnswers(response.data);
}

/** PUT /fantasy/answers/{slug} */
export async function requestSetFantasyAnswer(
  slug: string,
  answer: FantasyAnswerValue
): Promise<FantasyAnswersPayload> {
  const response = await api.put(
    `/fantasy/answers/${encodeURIComponent(slug)}`,
    { answer }
  );
  return normalizeFantasyAnswers(response.data);
}

/** DELETE /fantasy/answers/{slug} */
export async function requestClearFantasyAnswer(
  slug: string
): Promise<FantasyAnswersPayload> {
  const response = await api.delete(
    `/fantasy/answers/${encodeURIComponent(slug)}`
  );
  return normalizeFantasyAnswers(response.data);
}
